<script lang="ts">
  /**
   * Un nombre qui s'écrit, borné des deux côtés.
   *
   * La saisie en cours vit dans un brouillon plutôt que dans la valeur : tant
   * qu'on tape, personne ne recalcule une fiche sur un « 1 » qui allait
   * devenir « 12 ». La valeur ne part au domaine qu'une fois la frappe finie —
   * Entrée, ou le champ quitté. Échap annule.
   *
   * L'habillage est celui du design system (`saisie` + `data`) : un filet
   * discret qui se fond dans la page, des chiffres en sans tabulaire. Un champ
   * de la Création et un compteur de la fiche se ressemblent donc, parce que
   * c'est le même geste.
   */
  let {
    valeur,
    min = Number.NEGATIVE_INFINITY,
    max = Number.POSITIVE_INFINITY,
    libelle,
    pas = 1,
    taille = 'normal',
    onchange,
  }: {
    valeur: number
    min?: number
    max?: number
    libelle: string
    /** De combien avancent les flèches du clavier. */
    pas?: number
    taille?: 'normal' | 'grand' | 'petit'
    onchange: (v: number) => void
  } = $props()

  let brouillon = $state<string | null>(null)
  const affiche = $derived(brouillon ?? String(valeur))

  const poser = (v: number) => onchange(Math.min(max, Math.max(min, v)))

  function valider() {
    if (brouillon === null) return
    const saisi = Number.parseInt(brouillon, 10)
    brouillon = null
    // Une saisie vide ou illisible ne change rien : le champ se recolle à la
    // valeur en place plutôt que de la remettre au plancher.
    if (!Number.isNaN(saisi)) poser(saisi)
  }

  function auClavier(e: KeyboardEvent & { currentTarget: HTMLInputElement }) {
    if (e.key === 'Enter') {
      valider()
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      brouillon = null
      e.currentTarget.blur()
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
      brouillon = null
      poser(valeur + (e.key === 'ArrowUp' ? pas : -pas))
    }
  }
</script>

<input
  type="text"
  inputmode="numeric"
  class="saisie data nombre {taille}"
  value={affiche}
  aria-label={libelle}
  oninput={(e) => (brouillon = e.currentTarget.value)}
  onfocus={(e) => e.currentTarget.select()}
  onblur={valider}
  onkeydown={auClavier}
/>

<style>
  .nombre {
    text-align: center;
    padding: 0 calc(var(--pas));
  }
  .normal {
    width: 3.2rem;
  }
  /* Le premier nombre d'une fraction : c'est lui qu'on lit de loin. */
  .grand {
    width: 3.6em;
    font-size: 1.35rem;
    font-weight: 600;
  }
  /* Le second : le total, qui ne bouge qu'entre deux aventures. Assez large
     pour cinq chiffres — l'XP cumulé d'une longue campagne. */
  .petit {
    width: 4.4em;
    min-height: 1.9rem;
    font-size: 0.9rem;
  }
</style>
